// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Play tonicizations of a key for ear training examples.
//
import PianoInstrument from './pianoInstrument';
import utils from '../utils';
import KeySignature from './keySignature';
import progressions from './tonicizationProgressions';

export default class Tonicization {
  player: PianoInstrument;

  tonicizationsCount = 0;

  private _key: KeySignature;

  // Public constructor
  constructor(player: PianoInstrument, key?: KeySignature) {
    if (!(player instanceof PianoInstrument)) {
      throw new Error(
        'Not a player: To initialize a tonicization, you must provide a player as the first argument.'
      );
    }
    this.player = player;
    if (key) this.key = key;

    utils.shuffle(progressions);
  }

  get key() {
    return this._key;
  }

  set key(key) {
    this._key = KeySignature.stringToKey(key);
  }

  /**
   * Plays a tonicization, and returns a promise when that
   * tonicization is completed.
   */
  tonicize(key = this.key, tempo = 60) {
    if (!key) {
      throw new Error('Undefined Key: A key must be provided to generate a tonicization.');
    }
    this.key = key;
    this.tonicizationsCount = ++this.tonicizationsCount % progressions.length;
    const progression = progressions[this.tonicizationsCount];

    const midiMessages = progression.map((noteData) => {
      const [scaleDegree, octaveDisplacement, startBeat, stopBeat, velocity] = noteData;
      return this.getMessageForNote(scaleDegree, octaveDisplacement, startBeat, stopBeat, velocity);
    });

    this.player.playMidiMessages(midiMessages, tempo);

    const finalStopBeat = midiMessages[midiMessages.length - 1].startBeat + 2;
    const finalStopSecond = (finalStopBeat * 60) / tempo;
    return new Promise<void>((resolve) => {
      this.player.play('silence', { startTime: finalStopSecond }).onEnded(() => {
        resolve();
      });
    });
  }

  getMessageForNote(
    scaleDegree: number,
    octaveDisplacement: number,
    startBeat: number,
    stopBeat: number,
    velocity: number
  ) {
    const note = this.key.getNoteAtScaleDegree(scaleDegree, true);
    if (!note) {
      throw Error(`${scaleDegree} is not a valid scale degree in ${this.key}`);
    }
    note.octave += octaveDisplacement;
    return {
      midiNumber: note.getMidiNumber(),
      startBeat,
      stopBeat,
      velocity,
    };
  }
}
